/** The front door: what this service is, and the ways in.
 *
 * The buttons are rendered from `GET /api/providers` rather than written into
 * the page, because which providers exist is a decision the owner makes in
 * Admin → Settings and a self-hosted instance may make differently. A button
 * for a provider the server has switched off, or whose OAuth app was never
 * configured, would lead to a refusal the visitor cannot act on, so the
 * server's list is the only list.
 *
 * Signing in is a full page navigation to `/auth/<provider>/start`, never a
 * fetch. The provider has to redirect the browser and the callback has to set
 * a cookie on this origin; an XHR could do neither. That is also why these
 * are anchors rather than buttons — they go somewhere, they belong in the
 * browser's history, and middle-clicking one should behave.
 *
 * `redirect_to` carries the visitor back to whatever they were aiming at when
 * `RequireUser` sent them here. It is checked against a same-site path before
 * it is handed on: the server validates it too, but a portal that forwards
 * `//evil.example` to its own auth endpoint has already done the wrong thing.
 *
 * The right-hand outline is decoration with a point to make — it is drawn the
 * way every outline on this service is drawn, as a line somebody actually
 * traced — so it carries the design's travelling dash and a caption saying
 * so, rather than a stock photograph of a car.
 */

import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authStartUrl, getMeta, getProviders } from '@/lib/api';
import { useMe } from '@/lib/session';
import type { ProviderOption, ServiceMeta } from '@/lib/types';
import { ErrorNote, Kicker, Page, Spinner } from '@/components/ui';
import { ProviderMark } from '@/components/ProviderMark';
import { PLACEHOLDER_PATH } from '@/components/TrackOutline';

/** Where a signed-in visitor goes when nothing else was asked for. */
const DEFAULT_REDIRECT = '/sessions';

/** Used only if `/api/meta` cannot be reached; the fine print still has to
 *  name the repository that survey uploads become pull requests against. */
const TRACK_DATA_URL = 'https://github.com/jbhoorasingh/gt7-datalogger-track-data';
const DONATE_URL = 'https://github.com/sponsors/jbhoorasingh';

/**
 * A same-site path, or the default.
 *
 * Anything that is not a path rooted at `/` is discarded, and so is `//host`,
 * which a browser reads as a scheme-relative URL to somebody else's server.
 */
export function safeRedirect(raw: string | null | undefined): string {
  const value = (raw ?? '').trim();
  if (!value.startsWith('/') || value.startsWith('//')) return DEFAULT_REDIRECT;
  return value;
}

function HeartIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="var(--color-accent)"
      aria-hidden="true"
      style={{ flex: 'none' }}
    >
      <path d="M12 21s-7.5-4.6-9.5-9.3C1 7.8 3.4 4 7 4c2 0 3.6 1.1 5 3 1.4-1.9 3-3 5-3 3.6 0 6 3.8 4.5 7.7C19.5 16.4 12 21 12 21z" />
    </svg>
  );
}

// ── the outline ───────────────────────────────────────────────────────────

/** The design's hero drawing: a neutral bed with the accent walking round it.
 *  It is the stylised loop rather than a real circuit on purpose — no circuit
 *  should be advertised on a page that exists before you have surveyed any. */
function HeroOutline() {
  return (
    <div style={{ position: 'relative', minHeight: 320, display: 'grid', placeItems: 'center' }}>
      <svg
        viewBox="0 0 400 260"
        role="img"
        aria-label="A circuit outline with a marker travelling round it"
        style={{ width: '100%', maxWidth: 520, overflow: 'visible' }}
      >
        <path
          d={PLACEHOLDER_PATH}
          fill="none"
          stroke="var(--color-neutral-800)"
          strokeWidth="22"
          strokeLinejoin="round"
        />
        <path
          d={PLACEHOLDER_PATH}
          fill="none"
          stroke="var(--color-accent)"
          strokeWidth="1.5"
          strokeDasharray="6 394"
          strokeLinecap="round"
          style={{
            animation: 'dash 6s linear infinite',
            filter: 'drop-shadow(0 0 6px var(--color-accent))',
          }}
        />
        <line x1="150" y1="188" x2="150" y2="212" stroke="var(--color-neutral-300)" strokeWidth="2" />
        <text
          x="150"
          y="228"
          fill="var(--color-neutral-600)"
          fontSize="9"
          textAnchor="middle"
          fontFamily="var(--font-body)"
        >
          S/F
        </text>
      </svg>
      <p
        style={{
          margin: 0,
          fontSize: 11.5,
          color: 'var(--color-neutral-600)',
          textAlign: 'center',
          maxWidth: 420,
          textWrap: 'pretty',
        }}
      >
        Every outline on this service is drawn from borders somebody actually drove. Nothing is
        supplied by the game.
      </p>
    </div>
  );
}

// ── the buttons ───────────────────────────────────────────────────────────

interface ProviderButtonsProps {
  providers: ProviderOption[] | null;
  error: unknown;
  redirectTo: string;
  onRetry: () => void;
}

function ProviderButtons({ providers, error, redirectTo, onRetry }: ProviderButtonsProps) {
  if (error) {
    return (
      <ErrorNote
        error={error}
        title="The sign-in options could not be loaded."
        onRetry={onRetry}
        compact
      />
    );
  }

  if (providers === null) {
    return <Spinner label="loading sign-in options" />;
  }

  if (providers.length === 0) {
    return (
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--color-neutral-500)', textWrap: 'pretty' }}>
        No sign-in provider is switched on for this deployment. A provider needs two things:
        its OAuth app’s id and secret set on the Worker (<code>GITHUB_CLIENT_ID</code> and{' '}
        <code>GITHUB_CLIENT_SECRET</code>, or the same pair for Discord or Google), and its
        toggle on. The toggles are on by default, so on a fresh deployment the missing half
        is the secrets — and they cannot be set from in here, because nobody can sign in to
        reach Admin yet. Whoever runs the server sets them with{' '}
        <code>wrangler secret put</code>.
      </p>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6, maxWidth: 320 }}>
      {providers.map((provider, index) => (
        <a
          key={provider.name}
          className={`btn ${index === 0 ? 'btn-primary' : 'btn-secondary'}`}
          href={authStartUrl(provider.name, { redirectTo })}
          style={{ justifyContent: 'flex-start', padding: '10px 14px', fontSize: 14 }}
        >
          <ProviderMark name={provider.name} />
          Sign in with {provider.label}
        </a>
      ))}
    </div>
  );
}

// ── the screen ────────────────────────────────────────────────────────────

export default function SignIn() {
  const { me, loading: sessionLoading } = useMe();
  const location = useLocation();

  const [providers, setProviders] = useState<ProviderOption[] | null>(null);
  const [providerError, setProviderError] = useState<unknown>(null);
  const [meta, setMeta] = useState<ServiceMeta | null>(null);
  const [attempt, setAttempt] = useState(0);

  const redirectTo = useMemo(
    () => safeRedirect(new URLSearchParams(location.search).get('redirect_to')),
    [location.search],
  );

  useEffect(() => {
    let live = true;
    setProviders(null);
    setProviderError(null);

    void getProviders().then(
      (reply) => {
        if (live) setProviders(reply.providers ?? []);
      },
      (thrown: unknown) => {
        if (live) setProviderError(thrown);
      },
    );

    return () => {
      live = false;
    };
  }, [attempt]);

  // The donate and track-data links belong to the deployment. A failure here
  // falls back to this build's own repository rather than showing an error on
  // a page whose one job is to be walked past.
  useEffect(() => {
    let live = true;
    void getMeta().then(
      (value) => {
        if (live) setMeta(value);
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, []);

  // Nothing is drawn until the session is known, so somebody who is already
  // signed in never sees the sign-in page flash past on the way to their laps.
  if (sessionLoading) return null;
  if (me) return <Navigate to={redirectTo} replace />;

  const donateUrl = meta?.donate_url || DONATE_URL;
  const trackDataUrl = meta?.track_data_url || TRACK_DATA_URL;
  const trackDataName = meta?.track_data_repo.split('/')[1] ?? 'gt7-datalogger-track-data';
  const host = meta?.public_url.replace(/^https?:\/\//, '').replace(/\/$/, '') ?? 'sync.gt7-datalogger.com';

  return (
    <Page max={1100} center className="grid-cols-1 gap-10 md:grid-cols-2" label="Sign in">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 440 }}>
        <Kicker>{host}</Kicker>

        <h1 style={{ fontSize: 38, lineHeight: 1.08, margin: 0, textWrap: 'pretty' }}>
          Your laps in the cloud. Your surveys on the map.
        </h1>

        <p style={{ fontSize: 15, color: 'var(--color-neutral-400)', margin: 0, textWrap: 'pretty' }}>
          One connection string links your datalogger. Then choose what it shares: community track
          outlines, your own sessions, or a live stream for spectators. Each is opt-in, each is off
          until you say so.
        </p>

        <ProviderButtons
          providers={providers}
          error={providerError}
          redirectTo={redirectTo}
          onRetry={() => setAttempt((value) => value + 1)}
        />

        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            padding: '10px 12px',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-divider)',
            fontSize: 12,
            color: 'var(--color-neutral-400)',
            textWrap: 'pretty',
          }}
        >
          <HeartIcon />
          <span>
            This service costs me about $6–15 a month to run. If you like it,{' '}
            <a href={donateUrl} target="_blank" rel="noreferrer noopener" style={{ textDecoration: 'none' }}>
              a donation
            </a>{' '}
            keeps it up — ideally before my wife notices the sim-racing budget again.
          </span>
        </div>

        <p
          style={{
            fontSize: 11.5,
            color: 'var(--color-neutral-600)',
            margin: '6px 0 0',
            textWrap: 'pretty',
          }}
        >
          We store your provider login — for Google, that is your email address — and a display
          name. Tokens are hashed; the secret is shown once. Survey uploads are filed as pull requests against{' '}
          <a href={trackDataUrl} target="_blank" rel="noreferrer noopener">
            {trackDataName}
          </a>
          .
          {meta?.privacy_url ? (
            <>
              {' '}
              <a href={meta.privacy_url} target="_blank" rel="noreferrer noopener">
                Privacy
              </a>
            </>
          ) : null}
          {meta?.terms_url ? (
            <>
              {meta.privacy_url ? ' · ' : ' '}
              <a href={meta.terms_url} target="_blank" rel="noreferrer noopener">
                Terms
              </a>
            </>
          ) : null}
        </p>
      </div>

      <HeroOutline />
    </Page>
  );
}
